import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Loader2, Settings2, RefreshCw, CalendarClock, Wand2 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const eur = (v) => (v === null || v === undefined) ? '—' : Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const BANDS = ['A', 'B', 'C', 'D', 'E'];

function SettingsDialog({ open, onClose, onSaved }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/mid-term/settings')
      .then(res => setSettings(res.data))
      .catch(() => toast.error('Errore nel caricamento dei parametri'));
  }, [open]);

  const setDiscount = (setKey, band, val, variant = 'discounts') => setSettings(s => ({
    ...s,
    sets: { ...s.sets, [setKey]: { ...s.sets[setKey], [variant]: { ...s.sets[setKey][variant], [band]: val } } },
  }));
  const setReduction = (setKey, val) => setSettings(s => ({
    ...s, sets: { ...s.sets, [setKey]: { ...s.sets[setKey], final_reduction_pct: val } },
  }));
  const setComm = (k, val) => setSettings(s => ({ ...s, [k]: val }));

  const syncDates = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/mid-term/sync-hw-dates', {}, { timeout: 300000 });
      toast.success(`${res.data.updated} immobili aggiornati con la data di pubblicazione HW`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore sincronizzazione date');
    } finally { setSyncing(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const sets = {};
      Object.entries(settings.sets).forEach(([k, v]) => {
        const out = { final_reduction_pct: Number(v.final_reduction_pct || 0) };
        ['discounts', 'discounts_from_2025'].forEach(variant => {
          if (!v[variant]) return;
          const d = {};
          Object.entries(v[variant]).forEach(([b, p]) => { if (p !== '' && p !== null) d[b] = Number(p); });
          out[variant] = d;
        });
        sets[k] = out;
      });
      await api.put('/mid-term/settings', {
        sets,
        public_markup_pct: Number(settings.public_markup_pct),
        platform_pct: Number(settings.platform_pct),
        stripe_pct: Number(settings.stripe_pct),
        partner_pct: Number(settings.partner_pct),
      });
      toast.success('Parametri salvati');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const numCls = 'w-14 px-1.5 py-1 border rounded text-sm text-center';
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="midterm-settings-dialog">
        <DialogHeader>
          <DialogTitle>Parametri Medio Termine</DialogTitle>
          <DialogDescription>Sconti per fascia (%), riduzione finale (%) e parametri commerciali</DialogDescription>
        </DialogHeader>
        {!settings ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> : (
          <div className="space-y-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 border-b">
                  <th className="text-left py-1.5">Gruppo</th>
                  {BANDS.map(b => <th key={b} className="py-1.5">Fascia {b}</th>)}
                  <th className="py-1.5">Riduz. finale</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(settings.sets).map(([k, v]) => (
                  <React.Fragment key={k}>
                    <tr className="border-b last:border-0" data-testid={`midterm-set-row-${k}`}>
                      <td className="py-1.5 font-medium">{v.label || k}</td>
                      {BANDS.map(b => (
                        <td key={b} className="py-1.5 px-1 text-center">
                          <input type="number" value={v.discounts?.[b] ?? ''} placeholder="—"
                            onChange={(e) => setDiscount(k, b, e.target.value)}
                            className={numCls} data-testid={`midterm-discount-${k}-${b}`} />
                        </td>
                      ))}
                      <td className="py-1.5 px-1 text-center">
                        <input type="number" value={v.final_reduction_pct ?? ''}
                          onChange={(e) => setReduction(k, e.target.value)}
                          className={`${numCls} bg-amber-50`} data-testid={`midterm-reduction-${k}`} />
                      </td>
                    </tr>
                    {v.discounts_from_2025 && (
                      <tr className="border-b bg-blue-50/40">
                        <td className="py-1.5 text-xs text-blue-700 pl-3">↳ pubblicati dal 2025</td>
                        {BANDS.map(b => (
                          <td key={b} className="py-1.5 px-1 text-center">
                            <input type="number" value={v.discounts_from_2025?.[b] ?? ''} placeholder="—"
                              onChange={(e) => setDiscount(k, b, e.target.value, 'discounts_from_2025')}
                              className={`${numCls} border-blue-200`} data-testid={`midterm-discount2025-${k}-${b}`} />
                          </td>
                        ))}
                        <td />
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-600 mb-2">Parametri commerciali (prezzo al pubblico e margini)</p>
              <div className="grid grid-cols-4 gap-3">
                {[['public_markup_pct', 'Ricarico vendita %'], ['platform_pct', 'Piattaforme %'],
                  ['stripe_pct', 'Stripe %'], ['partner_pct', 'Partner %']].map(([k, lab]) => (
                  <div key={k}>
                    <label className="block text-[11px] text-slate-500 mb-0.5">{lab}</label>
                    <input type="number" value={settings[k] ?? ''} onChange={(e) => setComm(k, e.target.value)}
                      className="w-full px-2 py-1.5 border rounded text-sm text-center" data-testid={`midterm-comm-${k}`} />
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs text-slate-500">Sconti in % sul prezzo proprietario giornaliero. Riduzione finale sul totale mese (prima delle spese mq × 2 €). Vendita = base scontata + ricarico.</p>
            <div className="flex justify-between items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={syncDates} disabled={syncing} data-testid="midterm-sync-dates-btn">
                {syncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-1" />}
                Sincronizza date pubblicazione HW
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} data-testid="midterm-settings-cancel">Annulla</Button>
                <Button onClick={save} disabled={saving} data-testid="midterm-settings-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salva parametri'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MidTermSection({ propertyId, pub, setPubField, canEdit, dirty, isAdmin }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/mid-term/properties/${propertyId}/table`, { params: { year } });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel calcolo medio termine');
    } finally { setLoading(false); }
  }, [propertyId, year]);

  useEffect(() => { load(); }, [load]);

  const years = [];
  const cy = new Date().getFullYear();
  for (let y = cy - 1; y <= cy + 2; y++) years.push(y);

  return (
    <Card className="p-5 space-y-4" data-testid="scheda-mediotermine">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold text-slate-800">
            {isAdmin ? 'Medio Termine — catena completa (proprietario → pubblico → margini)' : 'Medio Termine — prezzo al pubblico'}
          </h3>
          {isAdmin && (
            <p className="text-xs text-slate-500 mt-0.5">
              Proprietario: giornaliero per fascia − sconto fascia → −riduzione finale → +spese mq×2 · Pubblico: base scontata +ricarico
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="px-2 py-1.5 border rounded-lg text-sm bg-white" data-testid="midterm-year-select">
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="midterm-reload-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)} data-testid="midterm-settings-btn">
              <Settings2 className="h-4 w-4 mr-1" /> Parametri
            </Button>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">MQ alloggio (reali, escluse parti esterne)</label>
            <input type="number" value={pub.mq ?? ''} disabled={!canEdit}
              onChange={(e) => setPubField('mq', e.target.value === '' ? null : Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Es. 80"
              data-testid="midterm-mq-input" />
            {data?.mq_suggested && !pub.mq ? (
              <button onClick={() => canEdit && setPubField('mq', data.mq_suggested)}
                className="mt-1 text-[11px] text-blue-600 hover:underline flex items-center gap-1" data-testid="midterm-mq-suggest-btn">
                <Wand2 className="h-3 w-3" /> Trovati {data.mq_suggested} mq nella descrizione — clicca per usarli
              </button>
            ) : (
              <p className="text-[11px] text-slate-400 mt-0.5">Contributo spese = mq × 2 €/mese</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Set sconti (override)</label>
            <select value={pub.mid_term_set || ''} disabled={!canEdit}
              onChange={(e) => setPubField('mid_term_set', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="midterm-set-select">
              <option value="">Automatico dal calendario{data?.calendar ? ` (${data.calendar})` : ''}</option>
              {(data?.available_sets || []).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <p className="text-[11px] text-slate-400 mt-0.5">Per i calendari "Personalizzato" scegli il set in base alla zona</p>
          </div>
          <div className="flex items-end">
            {dirty && <p className="text-xs text-amber-600 font-medium pb-2" data-testid="midterm-dirty-hint">⚠️ Salva la scheda per applicare mq / set, poi ricalcola</p>}
          </div>
        </div>
      )}

      {loading && !data ? (
        <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
      ) : data?.needs_set ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800" data-testid="midterm-needs-set">
          {data.message || 'Set sconti non configurato per questo immobile: contatta un amministratore.'}
        </div>
      ) : data ? (
        <>
          {isAdmin && (
            <div className="flex flex-wrap gap-2 text-xs" data-testid="midterm-recap">
              <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
                Set: {data.set_label} ({data.set_source})
              </span>
              <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                Sconti: {Object.entries(data.discounts || {}).map(([b, p]) => `${b} −${p}%`).join(' · ')}
              </span>
              {data.discounts_variant && (
                <span className="px-2 py-1 rounded-full bg-indigo-100 text-indigo-700" data-testid="midterm-variant-badge">{data.discounts_variant}</span>
              )}
              <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700">Riduzione finale −{data.final_reduction_pct}%</span>
              <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">Spese {eur(data.expenses_monthly)}/mese{data.mq ? ` (${data.mq} mq)` : ''}</span>
              <span className="px-2 py-1 rounded-full bg-violet-100 text-violet-700">Vendita = base +{data.public_markup_pct}%</span>
            </div>
          )}
          {(data.warnings || []).length > 0 && (
            <ul className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 space-y-0.5" data-testid="midterm-warnings">
              {data.warnings.map((w, i) => <li key={i}>⚠️ {w}</li>)}
            </ul>
          )}
          <div className="overflow-x-auto">
            {isAdmin ? (
              <table className="w-full text-sm whitespace-nowrap" data-testid="midterm-table">
                <thead>
                  <tr className="text-xs text-slate-500 border-b">
                    <th className="text-left py-2">Mese</th>
                    <th className="text-left py-2">Fasce</th>
                    <th className="text-right py-2">Base scontata</th>
                    <th className="text-right py-2 font-bold">Proprietario</th>
                    <th className="text-right py-2 font-bold text-violet-700">Vendita</th>
                    <th className="text-right py-2">Neto</th>
                    <th className="text-right py-2">Piattaf.</th>
                    <th className="text-right py-2">Stripe</th>
                    <th className="text-right py-2">Partner</th>
                    <th className="text-right py-2 font-bold text-emerald-700">RIM HW</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.months || []).map(m => (
                    <tr key={m.month} className="border-b last:border-0 hover:bg-slate-50" data-testid={`midterm-month-${m.month}`}>
                      <td className="py-1.5 font-medium">{m.label}</td>
                      <td className="py-1.5 text-xs text-slate-500">
                        {Object.entries(m.days_by_band || {}).map(([b, n]) => `${b}:${n}`).join(' ') || '—'}
                        {m.uncovered_days > 0 && <span className="text-red-500 ml-1">({m.uncovered_days} scop.)</span>}
                      </td>
                      <td className="py-1.5 text-right">{eur(m.base)}</td>
                      <td className="py-1.5 text-right font-bold" data-testid={`midterm-total-${m.month}`}>{eur(m.total)}</td>
                      <td className="py-1.5 text-right font-bold text-violet-700" data-testid={`midterm-vendita-${m.month}`}>{eur(m.vendita)}</td>
                      <td className="py-1.5 text-right text-slate-500">{eur(m.neto)}</td>
                      <td className="py-1.5 text-right text-slate-400">{m.piattaforme ? `−${eur(m.piattaforme)}` : '—'}</td>
                      <td className="py-1.5 text-right text-slate-400">{m.stripe ? `−${eur(m.stripe)}` : '—'}</td>
                      <td className="py-1.5 text-right text-slate-400">{m.partner ? `−${eur(m.partner)}` : '—'}</td>
                      <td className="py-1.5 text-right font-bold text-emerald-700" data-testid={`midterm-rim-${m.month}`}>{eur(m.rim)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 font-bold">
                    <td colSpan={3} className="py-2 text-right text-xs text-slate-500">TOTALE ANNUO</td>
                    <td className="py-2 text-right" data-testid="midterm-annual-total">{eur(data.annual_total)}</td>
                    <td className="py-2 text-right text-violet-700" data-testid="midterm-annual-vendita">{eur(data.annual_vendita)}</td>
                    <td colSpan={4} />
                    <td className="py-2 text-right text-emerald-700" data-testid="midterm-annual-rim">
                      {eur(data.annual_rim)}
                      {data.annual_vendita > 0 && <span className="block text-[10px] font-normal">{(data.annual_rim / data.annual_vendita * 100).toFixed(1)}% della vendita</span>}
                    </td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <table className="w-full max-w-md text-sm" data-testid="midterm-table-public">
                <thead>
                  <tr className="text-xs text-slate-500 border-b">
                    <th className="text-left py-2">Mese</th>
                    <th className="text-right py-2 font-bold">Prezzo al pubblico</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.months || []).map(m => (
                    <tr key={m.month} className="border-b last:border-0 hover:bg-slate-50" data-testid={`midterm-month-${m.month}`}>
                      <td className="py-1.5 font-medium">{m.label}
                        {m.uncovered_days > 0 && <span className="text-red-500 text-xs ml-1">({m.uncovered_days} gg scoperti)</span>}
                      </td>
                      <td className="py-1.5 text-right font-bold" data-testid={`midterm-vendita-${m.month}`}>{eur(m.vendita)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2">
                    <td className="py-2 text-right text-xs font-semibold text-slate-500">TOTALE ANNUO</td>
                    <td className="py-2 text-right font-bold text-blue-700" data-testid="midterm-annual-vendita">{eur(data.annual_vendita)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
          {!data.mid_term_flag && (
            <p className="text-xs text-slate-400" data-testid="midterm-flag-off-hint">
              ℹ️ Il flag "Medio termine" di questo immobile è spento: la tabella è solo di consultazione.
            </p>
          )}
        </>
      ) : null}

      {isAdmin && <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={load} />}
    </Card>
  );
}
